import { useMemo, useState } from 'react'

import type { ColDef } from 'ag-grid-enterprise'
import { format } from 'date-fns'

import { getDefaultCalendarRange, getHolidayYear } from '@fortem-monorepo/js-utilities'
import { AgGridWidget, createContextMenu } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Button, DataTableFacetedFilter, DateRangePicker, FormItem, Label } from '@fortem-monorepo/react-ui'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'
import type { Funds } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { FUND_CALENDAR } from '@/constants/links'
import { AddFundHolidaysDialog } from '@/pages/calendar/AddFundHolidaysDialog'
import { DeleteFundHolidayDialog, type HolidayToDelete } from '@/pages/calendar/DeleteFundHolidayDialog'
import { useFundHolidays, useFunds } from '@/react-query/queries/funds/funds'

const FundCalendar = () => {
  const [addingHoliday, setAddingHoliday] = useState(false)
  const [holidayToDelete, setHolidayToDelete] = useState<HolidayToDelete | null>(null)
  const [fundIds, setFundIds] = useState<string[]>([])
  const [range, setRange] = useState<{ from: Date | undefined; to?: Date }>(getDefaultCalendarRange)
  const { data: funds, query: fundsQuery } = useFunds()
  const completeRange = !!range.from && !!range.to && range.from <= range.to
  const holidaysQuery = useFundHolidays(
    {
      fund_ids: fundIds.map(Number),
      st_dt: range.from ? format(range.from, 'yyyy-MM-dd') : '',
      ed_dt: range.to ? format(range.to, 'yyyy-MM-dd') : '',
    },
    fundIds.length > 0 && completeRange,
  )
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        colId: 'year',
        headerName: 'Year',
        valueGetter: ({ data }) => getHolidayYear(data?.date),
        rowGroup: true,
        hide: true,
        sort: 'asc',
        editable: false,
      },
      { field: 'date', headerName: 'Date', sort: 'asc', cellDataType: 'dateString', editable: false },
      {
        colId: 'fund',
        headerName: 'Fund',
        valueGetter: ({ data }) => funds.find((fund) => fund.fund_id === data?.fund_id)?.fund_name ?? '',
        editable: false,
      },
      { field: 'comment', headerName: 'Comment', flex: 1, minWidth: 200, editable: false },
      { field: 'fund_id', hide: true, editable: false },
      { field: 'id', hide: true, editable: false },
    ],
    [funds],
  )

  return (
    <>
      {addingHoliday && (
        <AddFundHolidaysDialog funds={funds} initialFundIds={fundIds} onOpenChange={setAddingHoliday} />
      )}
      {holidayToDelete && (
        <DeleteFundHolidayDialog holiday={holidayToDelete} onClose={() => setHolidayToDelete(null)} />
      )}
      <HeaderConfigBar sticky>
        <div className="flex flex-wrap items-end gap-3">
          <FormItem>
            <Label>Funds</Label>
            <DataTableFacetedFilter
              size="sm"
              title="Funds"
              disabled={fundsQuery.isPending || fundsQuery.isError}
              options={funds.map((fund) => ({ label: fund.fund_name, value: String(fund.fund_id) }))}
              values={fundIds}
              setValues={setFundIds}
            />
          </FormItem>
          <FormItem>
            <Label>Date range</Label>
            <DateRangePicker date={range} setDate={setRange} size="sm" />
          </FormItem>
          <Button size="sm" variant="outline" onClick={() => setRange(getDefaultCalendarRange())}>
            Reset dates
          </Button>
          <Button
            size="sm"
            disabled={fundsQuery.isPending || fundsQuery.isError || !funds.length}
            onClick={() => setAddingHoliday(true)}
          >
            Add holidays
          </Button>
        </div>
      </HeaderConfigBar>
      {fundsQuery.isError && (
        <div role="alert" className="text-destructive">
          Unable to load funds. {getErrorMessage(fundsQuery.error)}{' '}
          <Button size="sm" variant="outline" onClick={() => void fundsQuery.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {!fundIds.length && <p className="text-muted-foreground">Select one or more funds to view their holidays.</p>}
      {fundIds.length > 0 && !completeRange && <p role="alert">Choose both a start and end date in order.</p>}
      {fundIds.length > 0 && completeRange && (
        <AgGridWidget
          query={holidaysQuery}
          widget={{ exposed: true, className: 'h-full' }}
          widgetHeader={{
            title: `Fund Holidays - ${fundIds.length} fund${fundIds.length === 1 ? '' : 's'}`,
          }}
          grid={{
            rowData: holidaysQuery.data ?? [],
            columnDefs,
            autoGroupColumnDef: { headerName: 'Year', minWidth: 130 },
            getContextMenuItems: createContextMenu<Funds.FundHoliday>((params) => {
              const row = params.node?.data
              const defaults = params.defaultItems ?? []
              if (params.node?.group || !row?.id) return defaults
              const fundName = funds.find((fund) => fund.fund_id === row.fund_id)?.fund_name ?? String(row.fund_id)
              const date = row.date.slice(0, 10)
              return [
                {
                  name: `Delete ${date} Holidays for ${fundName}?`,
                  cssClasses: ['text-red-600!'],
                  action: () => setHolidayToDelete({ id: row.id, date, fundName }),
                },
                'separator',
                ...defaults,
              ]
            }),
            domLayout: 'normal',
            suppressFieldDotNotation: true,
            overlayNoRowsTemplate: '<span>No holidays found for the selected funds in the selected range.</span>',
          }}
        />
      )}
    </>
  )
}

export const CalendarPage = () => (
  <Page metadata={{ title: 'Calendar' }} permissions={FUND_CALENDAR.permissions}>
    <FundCalendar />
  </Page>
)
